import { UsersActions } from '../actions/AdminActions';
import type { IAdminState } from '../appstate';
import {
	CreateAsynchronousActionReducer,
	CreateLoaderReducer,
	CreateResultSetReducer,
} from './GeneralReducers';

const initialState: IAdminState = {
	userAdmin: {
		users: {
			payload: [],
			inProcess: false,
			error: false,
			totalPages: 0,
			totalResults: 0,
		},
		customers: {
			payload: [],
			inProcess: false,
			error: false,
			totalPages: 0,
			totalResults: 0,
		},
		allCustomers: {
			payload: [],
			inProcess: false,
			error: false,
		},
		currentPage: 1,
		itemsPerPage: 20,
		searchTerm: '',
		selectedCustomerId: undefined,
		showUnassignedToCustomer: false,
		user: {
			payload: null,
			inProcess: false,
			error: false,
		},
		saveUserCustomers: {
			inProcess: false,
			error: false,
		},
		saveNewUser: {
			inProcess: false,
			error: false,
			payload: null,
		},
		saveChangePassword: {
			inProcess: false,
			error: false,
		},
		editUser: {
			inProcess: false,
			error: false,
			payload: null,
		},
		deleteUser: {
			inProcess: false,
			error: false,
		},
		userProfiles: {
			inProcess: false,
			error: false,
			payload: [],
		},
	},
};

export const _childReducers = {
	usersResultsReducer: CreateResultSetReducer({
		startAction: UsersActions.LOADING_USERS,
		clearPayloadWith: initialState.userAdmin.users.payload,
	}),
	userLoaderReducer: CreateLoaderReducer({
		startAction: UsersActions.LOADING_USER,
		clearPayloadWith: initialState.userAdmin.user.payload,
	}),
	userProfilesReducer: CreateLoaderReducer({
		startAction: UsersActions.LOADING_USER_PROFILES,
		clearPayloadWith: initialState.userAdmin.userProfiles.payload,
	}),
	customersResultsReducer: CreateResultSetReducer({
		startAction: UsersActions.LOADING_CUSTOMERS,
		clearPayloadWith: initialState.userAdmin.customers.payload,
	}),
	saveUserCustomersReducer: CreateAsynchronousActionReducer({
		startAction: UsersActions.SAVING_USER_CUSTOMERS,
	}),
	saveNewUserReducer: CreateLoaderReducer({
		startAction: UsersActions.SAVING_NEW_USER,
		clearPayloadWith: initialState.userAdmin.saveNewUser.payload,
	}),
	loadAllCustomersReducer: CreateLoaderReducer({
		startAction: UsersActions.LOADING_ALL_CUSTOMERS,
		clearPayloadWith: initialState.userAdmin.allCustomers.payload,
	}),
	saveChangePasswordReducer: CreateAsynchronousActionReducer({
		startAction: UsersActions.CHANGING_PASSWORD,
	}),
	editUserLoaderReducer: CreateLoaderReducer({
		startAction: UsersActions.EDITING_USER,
		clearPayloadWith: initialState.userAdmin.editUser.payload,
	}),
	deleteUserReducer: CreateAsynchronousActionReducer({
		startAction: UsersActions.DELETING_USER,
	}),
};

export function adminReducer(state: IAdminState = initialState, action) {
	switch (action.type) {
		case UsersActions.LOADING_USERS:
			return {
				...state,
				userAdmin: {
					...state.userAdmin,
					currentPage: action.payload.pageNumber,
					itemsPerPage: action.payload.itemsPerPage,
					searchTerm: action.payload.searchTerm,
					selectedCustomerId: action.payload.selectedCustomerId,
					showUnassignedToCustomer: action.payload.showUnassignedToCustomer,
					users: _childReducers.usersResultsReducer(
						state.userAdmin.users,
						action
					),
				},
			};
		case UsersActions.LOADING_USERS_SUCCESS:
		case UsersActions.LOADING_USERS_ERROR:
			return {
				...state,
				userAdmin: {
					...state.userAdmin,
					users: _childReducers.usersResultsReducer(
						state.userAdmin.users,
						action
					),
				},
			};
		case UsersActions.LOADING_USER_PROFILES:
		case UsersActions.LOADING_USER_PROFILES_SUCCESS:
		case UsersActions.LOADING_USER_PROFILES_ERROR:
			return {
				...state,
				userAdmin: {
					...state.userAdmin,
					userProfiles: _childReducers.userProfilesReducer(
						state.userAdmin.userProfiles,
						action
					),
				},
			};
		case UsersActions.LOADING_CUSTOMERS:
			return {
				...state,
				userAdmin: {
					...state.userAdmin,
					currentPage: action.payload.pageNumber,
					customers: _childReducers.customersResultsReducer(
						state.userAdmin.customers,
						action
					),
				},
			};
		case UsersActions.LOADING_CUSTOMERS_SUCCESS:
		case UsersActions.LOADING_CUSTOMERS_ERROR:
			return {
				...state,
				userAdmin: {
					...state.userAdmin,
					customers: _childReducers.customersResultsReducer(
						state.userAdmin.customers,
						action
					),
				},
			};
		case UsersActions.LOADING_ALL_CUSTOMERS:
		case UsersActions.LOADING_ALL_CUSTOMERS_SUCCESS:
		case UsersActions.LOADING_ALL_CUSTOMERS_ERROR:
			return {
				...state,
				userAdmin: {
					...state.userAdmin,
					allCustomers: _childReducers.loadAllCustomersReducer(
						state.userAdmin.allCustomers,
						action
					),
				},
			};
		case UsersActions.LOADING_USER:
		case UsersActions.LOADING_USER_SUCCESS:
		case UsersActions.LOADING_USER_ERROR:
			return {
				...state,
				userAdmin: {
					...state.userAdmin,
					user: _childReducers.userLoaderReducer(state.userAdmin.user, action),
				},
			};
		case UsersActions.SAVING_USER_CUSTOMERS:
		case UsersActions.SAVING_USER_CUSTOMERS_SUCCESS:
		case UsersActions.SAVING_USER_CUSTOMERS_ERROR:
			return {
				...state,
				userAdmin: {
					...state.userAdmin,
					saveUserCustomers: _childReducers.saveUserCustomersReducer(
						state.userAdmin.saveUserCustomers,
						action
					),
				},
			};
		case UsersActions.SAVING_NEW_USER:
		case UsersActions.SAVING_NEW_USER_SUCCESS:
		case UsersActions.SAVING_NEW_USER_ERROR:
			return {
				...state,
				userAdmin: {
					...state.userAdmin,
					saveNewUser: _childReducers.saveNewUserReducer(
						state.userAdmin.saveNewUser,
						action
					),
				},
			};
		case UsersActions.CHANGING_PASSWORD:
		case UsersActions.CHANGING_PASSWORD_SUCCESS:
		case UsersActions.CHANGING_PASSWORD_ERROR:
			return {
				...state,
				userAdmin: {
					...state.userAdmin,
					saveChangePassword: _childReducers.saveChangePasswordReducer(
						state.userAdmin.saveChangePassword,
						action
					),
				},
			};
		case UsersActions.EDITING_USER:
		case UsersActions.EDITING_USER_ERROR:
			return {
				...state,
				userAdmin: {
					...state.userAdmin,
					editUser: _childReducers.editUserLoaderReducer(
						state.userAdmin.editUser,
						action
					),
				},
			};
		case UsersActions.EDITING_USER_SUCCESS:
			return {
				...state,
				userAdmin: {
					...state.userAdmin,
					editUser: _childReducers.editUserLoaderReducer(
						state.userAdmin.editUser,
						action
					),
					user: {
						...state.userAdmin.user,
						payload: action.payload,
					},
				},
			};
		case UsersActions.DELETING_USER:
		case UsersActions.DELETING_USER_SUCCESS:
		case UsersActions.DELETING_USER_ERROR:
			return {
				...state,
				userAdmin: {
					...state.userAdmin,
					deleteUser: _childReducers.deleteUserReducer(
						state.userAdmin.deleteUser,
						action
					),
				},
			};
		case UsersActions.RESET_USER_FILTERS:
			return {
				...state,
				userAdmin: {
					...state.userAdmin,
					currentPage: 1,
					searchTerm: '',
					selectedCustomerId: undefined,
					showUnassignedToCustomer: false,
				},
			};
	}
	return state;
}
